import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Transfer, Tree } from 'antd';
import { space, width } from 'styled-system';

const StyledTransfer = styled(Transfer)`
  ${space}
  ${width}
  width: 100% !important;
`;

const getParentKey = (key, tree = []) => {
  let parentKey;
  for (let i = 0; i < tree.length; i++) {
    const node = tree[i];
    if (node.children) {
      if (node.children.some(item => item.key === key)) {
        parentKey = node.key;
      } else if (getParentKey(key, node.children)) {
        parentKey = getParentKey(key, node.children);
      }
    }
  }

  return parentKey;
};

const isInclude = (node, selectedKeys = []) => {
  return selectedKeys.includes(node.key) || node.children?.some(childNode => isInclude(childNode, selectedKeys));
};

const generateTree = (treeNodes = [], selectedKeys = []) =>
  treeNodes.map(({ children, loaded, ...props }) => ({
    ...props,
    disabled: !loaded || selectedKeys.includes(props.key),
    children: generateTree(children, selectedKeys),
  }));

const generateFilteredTree = (treeNodes = [], selectedKeys = []) =>
  treeNodes
    .filter(node => isInclude(node, selectedKeys))
    .map(({ children, ...props }) => ({
      ...props,
      children: generateFilteredTree(children, selectedKeys),
    }));

export function TreeTransferControl({
  settings: { dataSource = [], onLoadData, ...settings } = {},
  name,
  value = [],
  onChange,
  ...props
}) {
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [autoExpandParent, setAutoExpandParent] = useState(true);

  const onExpand = newExpandedKeys => {
    setExpandedKeys(newExpandedKeys);
    setAutoExpandParent(false);
  };

  const onSearch = useCallback(
    (_, value) => {
      const newExpandedKeys = dataSource
        .map(item => {
          if (item.title.toLocaleLowerCase().indexOf(value.toLocaleLowerCase()) > -1) {
            return getParentKey(item.key, dataSource);
          }

          return null;
        })
        .filter((item, i, self) => item && self.indexOf(item) === i);
      console.log(newExpandedKeys);
      setExpandedKeys(newExpandedKeys);
      setAutoExpandParent(true);
    },
    [dataSource]
  );

  const transferDataSource = useMemo(() => {
    const flatten = items => {
      return items.reduce((acc, curr) => {
        acc = acc.concat(curr);

        if (curr.children) {
          acc = acc.concat(flatten(curr.children));
        }

        return acc;
      }, []);
    };

    return flatten(dataSource);
  }, [dataSource]);

  const targetKeys = useMemo(() => value?.map(({ key }) => key), [value]);

  return (
    <StyledTransfer
      targetKeys={targetKeys}
      dataSource={transferDataSource}
      render={item => item.title}
      onChange={value =>
        onChange({ target: { value: value.map(key => transferDataSource.find(item => item.key === key)), name } })
      }
      onSearch={onSearch}
      {...props}
      {...settings}
    >
      {({ direction, onItemSelectAll, selectedKeys }) => {
        const onSelect = (_, { checked, checkedNodes }) => {
          onItemSelectAll(
            checkedNodes.map(({ key }) => key),
            checked
          );
        };

        const treeData =
          direction === 'left' ? generateTree(dataSource, targetKeys) : generateFilteredTree(dataSource, targetKeys);
        const checkedKeys = direction === 'left' ? [...selectedKeys, ...targetKeys] : selectedKeys;

        return (
          <Tree
            blockNode
            checkable
            defaultExpandAll
            checkedKeys={checkedKeys}
            treeData={treeData}
            loadData={onLoadData}
            onCheck={onSelect}
            onSelect={onSelect}
            height={300}
            onExpand={onExpand}
            expandedKeys={expandedKeys}
            autoExpandParent={autoExpandParent}
          />
        );
      }}
    </StyledTransfer>
  );
}

TreeTransferControl.propTypes = {
  name: PropTypes.string,
  value: PropTypes.any,
  placeholder: PropTypes.string,
  settings: PropTypes.object,
  hasError: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
};
